import { Check, Filter, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from 'components/shadcn/button';
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from 'components/shadcn/popover';
import { Input } from 'components/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/shadcn/select';
import { ConversationFilter } from '@/hooks/useConversationsQueryParams';
import { useTranslation } from 'react-i18next';
import { cn } from 'utils';

interface Props {
  dataKeys: string[];
  filters: ConversationFilter[];
  onApply: (key: string, value: string) => void;
  onRemove: (index: number) => void;
  onClear: () => void;
}

export function ConversationDataFilter({ dataKeys, filters, onApply, onRemove, onClear }: Props) {
  const { t } = useTranslation();
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const options = Array.from(new Set([...filters.map((filter) => filter.key), ...dataKeys])).sort();
  const canApply = Boolean(key && value.trim());

  useEffect(() => {
    setKey('');
    setValue('');
  }, [filters.length]);

  const apply = () => {
    if (canApply) onApply(key, value.trim());
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={filters.length ? 'secondary' : 'outline'}
          className="shrink-0 gap-2 shadow-sm transition-[box-shadow,transform] duration-200 hover:shadow-md active:scale-[0.96]"
          aria-label={t('dashboard.conversations.filters.ariaLabel')}
        >
          <Filter className="h-4 w-4" data-icon="inline-start" />
          {t('dashboard.conversations.filters.button')}
          {filters.length > 0 && (
            <span className="min-w-5 rounded-full bg-background px-1.5 text-center text-xs font-medium tabular-nums text-foreground">
              {filters.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(24rem,calc(100vw-2rem))] p-0">
        <PopoverHeader className="border-b px-4 py-3">
          <PopoverTitle>{t('dashboard.conversations.filters.title')}</PopoverTitle>
        </PopoverHeader>
        <div className="flex flex-col gap-4 p-4">
          {filters.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium">
                  {t('dashboard.conversations.filters.active', { count: filters.length })}
                </span>
                <Button type="button" variant="ghost" size="sm" onClick={onClear}>
                  {t('dashboard.conversations.filters.clear')}
                </Button>
              </div>
              <div
                className="flex flex-col gap-2"
                aria-label={t('dashboard.conversations.filters.ariaLabel')}
              >
                {filters.map((filter, index) => (
                  <div
                    key={`${filter.key}:${filter.value}:${index}`}
                    className="flex min-w-0 items-center justify-between gap-2 rounded-lg bg-muted/60 px-3 py-2 text-sm"
                  >
                    <span
                      className="truncate"
                      title={t('dashboard.conversations.filters.summary', {
                        field: filter.key,
                        value: filter.value,
                      })}
                    >
                      {t('dashboard.conversations.filters.summary', {
                        field: filter.key,
                        value: filter.value,
                      })}
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => onRemove(index)}
                      className="size-8 shrink-0"
                      aria-label={t('dashboard.conversations.filters.remove', {
                        field: filter.key,
                      })}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className={cn('flex flex-col gap-3', filters.length > 0 && 'border-t pt-4')}>
            <span className="text-sm font-medium">
              {t('dashboard.conversations.filters.addTitle')}
            </span>
            <Select value={key} onValueChange={setKey} disabled={!options.length}>
              <SelectTrigger>
                <SelectValue placeholder={t('dashboard.conversations.filters.fieldPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={value}
              onChange={(event) => setValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') apply();
              }}
              placeholder={t('dashboard.conversations.filters.valuePlaceholder')}
              aria-label={t('dashboard.conversations.filters.valueAriaLabel')}
              disabled={!key}
            />
            <Button type="button" onClick={apply} disabled={!canApply} className="w-full">
              <Check data-icon="inline-start" />
              {t('dashboard.conversations.filters.add')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
